import { PropsWithChildren } from 'react';

import { FatalAttraction, GoogleTagManager as GTM } from '@vpr/tracking';

import { ArrowRight } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { Paragraph } from '@vimeo/iris/typography';

import Container from './Container';
import {
  getFAComponentStringViaFeatureName,
  getGTMEventStringViaFeatureName,
} from './getStringsForLogging';
import {
  ImageWrapper,
  DescriptionWrapper,
  FeatureDescription,
  SectionHeader,
  SectionTextWrapper,
  LearnMore,
  TitleWrapper,
  Wrapper,
  FeatureSectionWrapper,
} from './index.styles';

interface FeatureSectionProps {
  descriptions: Array<string>;
  flexContentWidth?: Array<number>;
  flexJustifyContent?: string;
  linkDescription?: string;
  name: string;
  includeLearnMore?: boolean;
  isFirst?: boolean;
  isSecond?: boolean;
  title: string;
  url: string;
}

const FeatureSection: React.FC<FeatureSectionProps & PropsWithChildren> = ({
  children,
  descriptions,
  includeLearnMore = true,
  linkDescription,
  isFirst,
  isSecond,
  name,
  title,
  url,
}) => {
  return (
    <Wrapper isFirst={isFirst} isSecond={isSecond}>
      <Container>
        <FeatureSectionWrapper>
          {children && (
            <ImageWrapper>
              <div aria-hidden="true">{children}</div>
            </ImageWrapper>
          )}
          <DescriptionWrapper isFirst={isFirst}>
            <SectionTextWrapper isFirst={isFirst}>
              <TitleWrapper type="secondary" flush>
                <SectionHeader>
                  {title}
                  <span aria-hidden="true">&nbsp;</span>
                </SectionHeader>
              </TitleWrapper>
              {descriptions.map((description) => (
                <FeatureDescription key={description}>
                  <Paragraph size={1}>{description}</Paragraph>
                </FeatureDescription>
              ))}
              {includeLearnMore && (
                <LearnMore
                  title={`Learn more about '${title}' link`}
                  href={url}
                  variant="minimal"
                  onClick={() => {
                    FatalAttraction.trackClick({
                      container: 'lohp',
                      ref_path: window.location.pathname,
                      target: url,
                      keyword: 'learn_more',
                      component: getFAComponentStringViaFeatureName(name),
                    });
                    GTM.trackEvent(getGTMEventStringViaFeatureName(name, true), {
                      event: getGTMEventStringViaFeatureName(name, true),
                      category: 'lohp_features',
                      action: getFAComponentStringViaFeatureName(name),
                      label: 'learn_more',
                    });
                  }}
                >
                  {linkDescription
                    ? translate('Learn more ') + linkDescription
                    : translate('Learn more')}
                  <br />
                  <ArrowRight width="10px" color="vimeoBlue.500" />
                </LearnMore>
              )}
            </SectionTextWrapper>
          </DescriptionWrapper>
        </FeatureSectionWrapper>
      </Container>
    </Wrapper>
  );
};

export default FeatureSection;
